import React from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';
import { Link } from 'react-router-dom';
import { RBaseButton } from './RBaseButton';

interface RPillButtonProps {
    children: React.ReactNode;
    onClick?: () => void;
    to?: string;
    href?: string;
    variant?: 'primary' | 'secondary';
    icon?: React.ReactNode;
    disabled?: boolean;
    color?: string;
    hoverColor?: string;
}

export function RPillButton({
    children,
    onClick,
    to,
    href,
    variant = 'primary',
    icon,
    disabled,
    color,
}: RPillButtonProps) {
    const isPrimary = variant === 'primary';

    const colorStyle = color ? (isPrimary ? { backgroundColor: color } : { borderColor: color, color }) : undefined;

    if (href && !disabled) {
        const AnchorComponent = isPrimary ? PrimaryPillAnchor : SecondaryPillAnchor;
        return (
            <AnchorComponent href={href} style={colorStyle}>
                {icon}
                {children}
            </AnchorComponent>
        );
    }

    if (to && !disabled) {
        const LinkComponent = isPrimary ? PrimaryPillLink : SecondaryPillLink;
        return (
            <LinkComponent to={to} style={colorStyle}>
                {icon}
                {children}
            </LinkComponent>
        );
    }

    const Button = isPrimary ? PrimaryPill : SecondaryPill;
    return (
        <Button onClick={onClick} disabled={disabled} style={colorStyle}>
            {icon}
            {children}
        </Button>
    );
}

const basePill = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '8px 24px',
    borderRadius: 24,
    fontSize: 14,
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    textDecoration: 'none',
};

const PrimaryPill = styled(RBaseButton)({
    ...basePill,
    backgroundColor: Colors.darkNavy,
    color: Colors.white,
    '&:hover:not(:disabled)': {
        backgroundColor: Colors.pressedNavy,
        color: Colors.white,
    },
});

const SecondaryPill = styled(RBaseButton)({
    ...basePill,
    border: `1px solid ${Colors.lightGrey}`,
    backgroundColor: Colors.white,
    color: Colors.darkNavy,
    '&:hover:not(:disabled)': {
        backgroundColor: Colors.lightGrey,
    },
});

const PrimaryPillLink = styled(Link)({
    ...basePill,
    border: 'none',
    backgroundColor: Colors.darkNavy,
    color: Colors.white,
    '&:hover': {
        backgroundColor: Colors.pressedNavy,
        color: Colors.white,
        textDecoration: 'none',
    },
});

const SecondaryPillLink = styled(Link)({
    ...basePill,
    border: `1px solid ${Colors.lightGrey}`,
    backgroundColor: Colors.white,
    color: Colors.darkNavy,
    '&:hover': {
        backgroundColor: Colors.lightGrey,
        textDecoration: 'none',
    },
});

const PrimaryPillAnchor = styled('a')({
    ...basePill,
    border: 'none',
    backgroundColor: Colors.darkNavy,
    color: Colors.white,
    '&:hover': {
        backgroundColor: Colors.pressedNavy,
        color: Colors.white,
        textDecoration: 'none',
    },
});

const SecondaryPillAnchor = styled('a')({
    ...basePill,
    border: `1px solid ${Colors.lightGrey}`,
    backgroundColor: Colors.white,
    color: Colors.darkNavy,
    '&:hover': {
        backgroundColor: Colors.lightGrey,
        color: Colors.darkNavy,
        textDecoration: 'none',
    },
});
